/** The three key document slots on a project.
 *
 * Every slot is shown whether or not it is filled, with a tick or a cross. Hiding empty
 * slots would make the missing charter invisible, which is the opposite of the point.
 *
 * Generating produces a document already carrying the project's facts and writes it to
 * the project's own SharePoint folder. It never opens a shared master template — in the
 * pilot that risked one project's edits landing in another's document.
 */

import { Check, ExternalLink, FileText, X } from 'lucide-react'

import { Button, Can, ErrorState, Loading, Pill, SectionHeader } from '@/components/ui'
import { stageLabel } from '@/lib/domain'
import { Permissions } from '@/lib/session'
import {
  useGenerateKeyDocument,
  useKeyDocuments,
  type KeyDocumentSlot,
} from '@/features/documents/useDocuments'

function SlotRow({ projectId, slot }: { projectId: string; slot: KeyDocumentSlot }) {
  const generate = useGenerateKeyDocument(projectId)

  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3">
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        aria-hidden
        style={{
          backgroundColor: slot.present
            ? 'color-mix(in srgb, var(--status-green) 15%, transparent)'
            : 'color-mix(in srgb, var(--fg-muted) 12%, transparent)',
          color: slot.present ? 'var(--status-green)' : 'var(--fg-muted)',
        }}
      >
        {slot.present ? <Check size={14} /> : <X size={14} />}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-fg">{slot.label}</span>
          {/* Expected by this stage and the project has moved past it. Said plainly,
              and it blocks nothing. */}
          {slot.overdue ? <Pill tone="amber">Expected by {stageLabel(slot.expectedStage)}</Pill> : null}
        </div>
        <p className="text-xs text-fg-muted">
          {slot.present
            ? `Generated ${slot.generatedAt ? new Date(slot.generatedAt).toLocaleDateString() : ''}`
            : `Usually written during ${stageLabel(slot.expectedStage)}`}
        </p>
      </div>

      <div className="flex items-center gap-2">
        {slot.webUrl ? (
          <a
            href={slot.webUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm font-semibold text-fg hover:bg-canvas"
          >
            <ExternalLink size={14} aria-hidden />
            Open
          </a>
        ) : null}
        <Can permission={Permissions.documentsManage}>
          <Button
            variant={slot.present ? 'secondary' : 'primary'}
            onClick={() => generate.mutate(slot.slot)}
            disabled={generate.isPending}
          >
            <FileText size={14} aria-hidden />
            {generate.isPending ? 'Generating…' : slot.present ? 'Regenerate' : 'Generate'}
          </Button>
        </Can>
      </div>

      {generate.error ? (
        <div className="w-full">
          <ErrorState error={generate.error} />
        </div>
      ) : null}
    </li>
  )
}

export function KeyDocuments({ projectId }: { projectId: string }) {
  const { data, isPending, error } = useKeyDocuments(projectId)

  return (
    <section>
      <SectionHeader>Key documents</SectionHeader>
      {isPending ? (
        <Loading label="Loading documents" />
      ) : error ? (
        <ErrorState error={error} />
      ) : (
        <>
          <ul className="card divide-y divide-border">
            {(data?.slots ?? []).map((slot) => (
              <SlotRow key={slot.slot} projectId={projectId} slot={slot} />
            ))}
          </ul>
          <p className="mt-2 text-xs text-fg-muted">
            Generated documents are prefilled from this project and saved to its own folder in
            SharePoint. Regenerating replaces the file, so edit in Word rather than regenerating
            after you have written into it.
          </p>
        </>
      )}
    </section>
  )
}
